/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Divider } from "@mui/material";
import { ToastContainer } from "react-toastify";
import { useOrderDetails } from "./saloon-order-display/hooks/useOrderDetails";
import OrderHeader from "./saloon-order-display/components/OrderHeader";
import OrderSummary from "./saloon-order-display/components/OrderSummary";
import OrderItemsTable from "./saloon-order-display/components/OrderItemsTable";
import PaymentDialog from "./saloon-order-display/components/PaymentDialog";
import CancelOrderDialog from "./saloon-order-display/components/CancelDialog";
import { useCancelHandler } from "./saloon-order-display/hooks/useCancelHandler";
import { usePaymentHandler } from "./saloon-order-display/hooks/usePaymentHandler";
import OrderActions from "./saloon-order-display/components/OrderActions";
import { PrintData } from "src/print-configuration/PrintConfigration";
import { splitDateTime } from "src/shared/components/dateTime";
import { useUserTheme } from "src/shared/hooks/useUserTheme";

const SaloonOrderDisplay: React.FC = () => {
  const navigate = useNavigate();
  const { orderid } = useParams();
  const { order, isLoading, outstandingAmount, fetchOrderDetails, updateOrderStatus } = useOrderDetails(orderid);
  const [printOpen, setPrintOpen] = useState<boolean>(false);
  const { paymentData, openPaymentDialog, setOpenPaymentDialog, isProcessing, handlePaymentInputChange, processPayment, resetPaymentData, openPaymentWithMethod } = usePaymentHandler(orderid, order, outstandingAmount, fetchOrderDetails);
  const { date: orderDate, time: orderTime } = splitDateTime(order?.orderDetails[0].orderedOn.$date ?? "");
  const { cancelData, openCancelDialog, setOpenCancelDialog, handleCancelOrder, setCancelData } = useCancelHandler(updateOrderStatus);
  const ordersList = order?.orders || [];
  const firstOrder = ordersList[0];
  const lastOrder = ordersList[ordersList.length - 1];
  const paymentHistory = order?.paymentHistory || [];

  const customerName = [order?.customerDetails?.firstName, order?.customerDetails?.middleName, order?.customerDetails?.lastName]
    .filter(Boolean)
    .join(" ");
  const { sidebarThemeClass } = useUserTheme();

  const formattedProducts = order?.orderDetails[0]?.products.map((p: any) => ({
    productName: p.productName || p.name || "",
    arabicword: p.arabicName || p.arabicword || "",
    amount: Number(p.unitPrice || p.price || p.amount || 0),
    quantity: Number(p.quantity || 1),
    totalPrice: Number(p.lineTotal ?? p.itemTotal ?? (Number(p.unitPrice || p.price || 0) * Number(p.quantity || 1))),
  }));

  const latestPayment = paymentHistory.length > 0 ? paymentHistory[paymentHistory.length - 1] : null;

  // CURRENT PAID (The latest payment in paymentHistory or lastOrder receivedAmount)
  const paid = latestPayment
    ? Number(latestPayment.receivedAmount || 0)
    : (lastOrder?.receivedAmount !== undefined ? Number(lastOrder.receivedAmount) : 0);

  // PREVIOUS PAID (Sum of all earlier payments)
  const prevPaid = paymentHistory.length > 1
    ? paymentHistory.slice(0, -1).reduce((sum, item) => sum + Number(item?.receivedAmount || 0), 0)
    : 0;

  // TOTAL PAID SO FAR
  const totalPaidSoFar = paymentHistory.reduce((sum, item) => sum + Number(item?.receivedAmount || 0), 0);

  const orderGrossTotal = Number(firstOrder?.orderAmount ?? 0);
  const orderNetTotal = Number(firstOrder?.finalAmount ?? orderGrossTotal);

  // REMAINING AMOUNT (Directly from GET Order API)
  const remainingAmountValue = lastOrder?.remainingAmount !== undefined
    ? Number(lastOrder.remainingAmount)
    : Math.max(0, orderNetTotal - totalPaidSoFar);

  // PREVIOUS BALANCE
  const prevBalance = Math.max(0, orderNetTotal - prevPaid);

  const printData: PrintData = {
    CustomerName: customerName || "",
    CustomerPhon: order?.customerDetails?.phones?.[0]?.phone || "",
    OrderId: firstOrder?.orderId ?? lastOrder?.orderId ?? "",
    OrderStatus: lastOrder?.status || firstOrder?.status || "",
    discount: Number(firstOrder?.discount || 0),
    Date: orderDate || "",
    Time: orderTime || "",
    Products: formattedProducts,

    // CURRENT PAID
    Paid: paid,

    // PREVIOUS PAID
    prvPaid: prevPaid,

    // PREVIOUS BALANCE
    prvBalance: prevBalance,

    // TOTAL REMAINING (Unpaid directly from GET Order API)
    unpaid: remainingAmountValue,

    // OUTSTANDING (directly from GET Order API)
    OSB: Number(order?.totalOutstandingBalance ?? lastOrder?.outstandingBalance ?? outstandingAmount ?? 0),

    paymentMethod: latestPayment?.paymentMethod || "",

    total: orderGrossTotal,

    subtotal: orderNetTotal,

    balanceReturned: Number(lastOrder?.balanceReturned || 0),
  };

  const handleGoBack = useCallback(() => {
    setOpenPaymentDialog(false);
    setOpenCancelDialog(false);
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    document.title = "Order › Details › iMetanic";
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center bg-white rounded-xl shadow-lg p-8">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-600 text-lg">Order not found</p>
        </div>
      </div>
    );
  }

  const remainingAmount = Number(lastOrder?.remainingAmount) || 0;

  return (
    <div className="h-screen p-3 bg-gray-50">
      <div className="h-full max-w-7xl mx-auto">
        <div className={`h-full flex flex-col bg-white rounded-xl shadow-lg overflow-hidden ${sidebarThemeClass}`}>
          {/* Header Section - Fixed Height */}
          <div className="flex-shrink-0 px-4 pt-4 pb-2">
            <OrderHeader 
              order={order} 
              outstandingAmount={outstandingAmount} 
              onPrint={() => setPrintOpen(!printOpen)} 
              onGoBack={handleGoBack} 
              printData={printData} 
            />
          </div>

          <Divider sx={{ my: 0 }} />

          {/* Main Content - Flexible Height with proper spacing */}
          <div className="flex-1 min-h-0 px-4 py-3 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-full">
              {/* Order Items Section - 7 columns on large screens */}
              <div className="lg:col-span-7 flex flex-col min-h-0">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2 flex-shrink-0">
                  Order Items
                </h3>
                <div className="flex-1 min-h-0 overflow-hidden">
                  <OrderItemsTable order={order} />
                </div>
              </div>

              {/* Right Column - 5 columns on large screens */}
              <div className="lg:col-span-5 flex flex-col min-h-0 gap-3">
                {/* Order Summary - takes remaining space */}
                <div className="flex-1 min-h-0">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2 flex-shrink-0">
                    Order Summary
                  </h3>
                  <div className="h-[calc(100%-28px)]">
                    <OrderSummary order={order} />
                  </div>
                </div>

                {/* Order Actions - auto height */}
                <div className="flex-shrink-0">
                  <OrderActions
                    order={order}
                    onUpdateStatus={updateOrderStatus}
                    onOpenPayment={openPaymentWithMethod}
                    onOpenCancel={() => setOpenCancelDialog(true)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PaymentDialog
        open={openPaymentDialog}
        onClose={resetPaymentData}
        order={order}
        paymentData={paymentData}
        onPaymentDataChange={handlePaymentInputChange}
        onProcessPayment={processPayment}
        isProcessing={isProcessing}
      />

      <CancelOrderDialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        order={order}
        outstandingAmount={outstandingAmount}
        cancelData={cancelData}
        onCancelDataChange={setCancelData}
        onConfirmCancel={handleCancelOrder}
      />

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default SaloonOrderDisplay;